import styled from 'styled-components';
import * as React from 'react';

import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { getZIndex } from '@process-advisor/process-miner/shared/theme';
import { Event } from '@process-advisor/process-miner/api/events';
import { getVisualizerBackgroundColorDiagram, getProcessModelTitle, getModelID, getAppVersion, getSourceSystem, setDiagram } from '@process-advisor/process-miner/processMiner/processMiner';
import { useApiListener } from '@process-advisor/process-miner/shared/hooks/useApiListener';
import { Costs } from '@process-advisor/process-miner/cost/costs';
import { ProcessMinerGauges } from '@process-advisor/process-miner/navigation/topBar/processMinerGauges';
import { DecompositionTree_Options } from '@process-advisor/process-miner/components/decompositionTree/options';
import { Cluster_Options } from '@process-advisor/process-miner/components/cluster/options';
import { InfluenceAnalysis_Options } from '@process-advisor/process-miner/components/influencer/options';
import { Analytics } from '@process-advisor/process-miner/analytics/analytics';
import { DiscoveryRules } from '@process-advisor/process-miner/discoveryRules/discoveryRules';
import ProcessDashboard from '@process-advisor/process-miner/dashboard/processDashboard';
import ConsultantDashboard from '@process-advisor/process-miner/consultant/consultantDashboard';
import { SnapshotListPanel } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { Model } from '@process-advisor/process-miner/model/model';
import { TimeInActivity } from '@process-advisor/process-miner/time/timeInActivity';
import { WorkFlow } from '@process-advisor/process-miner/workflows/workFlow';
import Home from '@process-advisor/process-miner/home/Home';
import ValueSimulation from '@process-advisor/process-miner/value/ValueSimulation';
import { getCapabilitiesForSource } from '@process-advisor/process-miner/sources/capabilities';

const Diagram = styled.div<{ color: string; $visible: boolean }>`
  background-color: ${({ color }) => color};
  width: 100%;
  height: 100%;
  border: hidden;
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  canvas {
    outline: 0 none !important;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  }
  z-index: ${getZIndex('diagram')};
`;

const Wrapper = styled.div`
  height: 100%;
  min-height: 0;
`;

const Grid = styled.div<{ height: string }>`
  display: grid;
  grid-template-rows: ${({ height }) => height} 1fr;
  height: 100%;
  min-height: 0;
`;

const TopWrapper = styled.div<{ color: string }>`
  grid-row: 1;
  grid-column: 1;
  background-color: ${({ color }) => color};
`;

const BottomWrapper = styled.div`
  grid-row: 2;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  grid-column: 1;
`;

const getCurrentCapabilities = () => getCapabilitiesForSource(getSourceSystem ? getSourceSystem() : 'generic');

const normalizeDashboardModeForCapabilities = (mode?: string) => {
  const capabilities = getCurrentCapabilities();
  if (mode === 'activityMetrics' && !capabilities.activityMetrics) return 'metrics';
  if (mode === 'slaMetrics' && !capabilities.slaMetrics) return 'metrics';
  if (mode === 'segments' && !capabilities.evidenceSegments) return 'metrics';
  return mode;
};

type VisualizerProps = {
  visible?: boolean;
  action?: string;
  app?: string | undefined;
  mode?: string;
  customer_id?: string;
  customer?: any;
  loadEventLog?: boolean;
  AppVersion?: string;
  contextPath?: any;
  AI?: any;
  license?: any;
  licenseType?: any;
  forgeContext?: any;
  licenseInfo?: any;
  appEdition?: string;
  editionAccess?: any;
  processLabel?: string;
  entityLabel?: string;
  onModeChange?: (mode: string) => void;
  appTitle?: string;
  model?: any;
  snapshotCount?: number;
  setSnapshotCount?: (n: number) => void;
  snapshotPanelOpen?: boolean;
  setSnapshotPanelOpen?: (open: boolean) => void;
};

export const Visualizer: React.FC<VisualizerProps> = ({
  mode,
  customer_id,
  customer,
  AppVersion,
  contextPath,
  AI,
  license,
  licenseType,
  forgeContext,
  licenseInfo,
  appEdition = 'standard',
  editionAccess,
  processLabel = 'Process',
  entityLabel = 'Work Item',
  onModeChange,
  appTitle = 'Process Advisor',
  model = {},
  snapshotCount,
  setSnapshotCount,
  snapshotPanelOpen,
  setSnapshotPanelOpen,
}) => {
  const [component, setComponent] = React.useState<string>('home');
  const [topWrapperHeight, setTopWrapperHeight] = React.useState<string>('0px');
  const [visiblePV, setVisiblePV] = React.useState<boolean>(false);
  const [visibleDT, setVisibleDT] = React.useState<boolean>(false);
  const [visibleIS, setVisibleIS] = React.useState<boolean>(false);
  const [visibleIA, setVisibleIA] = React.useState<boolean>(false);
  const [visualizerBackground, setVisualizerBackground] = React.useState<string>('');

  const [aiInsightsState, setAiInsightsState] = React.useState({
    insights: null,
    insightsGenerated: false,
    focusType: null,
    modelTitle: '',
  });

  const effectiveMode = normalizeDashboardModeForCapabilities(mode) || mode;

  React.useEffect(() => {
    setVisualizerBackground(getVisualizerBackgroundColorDiagram());
    const { diagram } = apiProvider.init();
    apiProvider.setDiagram(diagram);
    setDiagram(mode);
  }, []);

  React.useEffect(() => {
    const listener = () => setVisualizerBackground(getVisualizerBackgroundColorDiagram());
    listener();
    apiProvider.on(Event.changeVisualizerBackgroundColor, listener);
    return () => {
      apiProvider.off(Event.changeVisualizerBackgroundColor, listener);
    };
  }, []);

  const navigateHomeTarget = React.useCallback(
    async (nextMode: string) => {
      nextMode = normalizeDashboardModeForCapabilities(nextMode) || nextMode;
      onModeChange?.(nextMode);

      if (
        nextMode === 'quickHealthcheck' ||
        nextMode === 'processHealth' ||
        nextMode === 'trendAnalysis' ||
        nextMode === 'aiIntelligence' ||
        nextMode === 'aiExecutionStrategy' ||
        nextMode === 'automationPacks' ||
        nextMode === 'frameworkAssessment' ||
        nextMode === 'consultingReport'
      ) {
        apiProvider.setAction_Consultant({});
        return;
      }

      if (nextMode === 'metrics' || nextMode === 'activityMetrics' || nextMode === 'slaMetrics' || nextMode === 'segments' || nextMode === 'variants' || nextMode === 'transitions') {
        apiProvider.setAction_Dashboard({});
        return;
      }

      if (nextMode === 'valueSimulation') {
        return;
      }

      if (nextMode === 'performance_discovery_rules' || nextMode === 'compliance_discovery_rules') {
        apiProvider.setAction_DiscoveryRulesSummary({});
        return;
      }

      if (nextMode === 'discovery_rules_visualizer') {
        apiProvider.setAction_DiscoveryRulesVisualizer({});
        return;
      }

      if (nextMode === 'process_miner') {
        apiProvider.setAction_Visualizer({});
        apiProvider.setDiagramVisualizer();
        return;
      }
    },
    [onModeChange]
  );

  React.useEffect(() => {
    if (effectiveMode === 'home') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('home');
    }

    if (effectiveMode === 'process_miner') {
      setTopWrapperHeight('75px');
      setVisiblePV(true);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('PV');
    }

    if (effectiveMode === 'time_in_activity') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('time');
    }

    if (effectiveMode === 'decomposition_tree') {
      setTopWrapperHeight('135px');
      setVisiblePV(false);
      setVisibleDT(true);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('DT');
    }

    if (effectiveMode === 'cluster_insights') {
      setTopWrapperHeight('175px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(true);
      setVisibleIA(false);
      setComponent('CL');
    }

    if (effectiveMode === 'influence_analysis') {
      setTopWrapperHeight('175px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(true);
      setComponent('IA');
    }

    if (effectiveMode === 'performance_discovery_rules') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('performance_discovery_rules');
    }

    if (effectiveMode === 'compliance_discovery_rules') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('compliance_discovery_rules');
    }

    if (effectiveMode === 'discovery_rules_visualizer') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('discovery_rules_visualizer');
    }

    if (effectiveMode === 'model') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('model');
    }

    if (effectiveMode === 'analytics') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('analytics');
    }

    if (
      effectiveMode === 'quickHealthcheck' ||
      effectiveMode === 'processHealth' ||
      effectiveMode === 'trendAnalysis' ||
      effectiveMode === 'aiIntelligence' ||
      effectiveMode === 'aiExecutionStrategy' ||
      effectiveMode === 'automationPacks' ||
      effectiveMode === 'frameworkAssessment' ||
      effectiveMode === 'consultingReport' ||
      effectiveMode === 'consultingWorkspace' ||
      effectiveMode === 'workbenchFindings' ||
      effectiveMode === 'workbenchActions' ||
      effectiveMode === 'processSnapshot' ||
      effectiveMode === 'consultant'
    ) {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('consultant');
    }

    if (effectiveMode === 'metrics' || effectiveMode === 'activityMetrics' || effectiveMode === 'slaMetrics' || effectiveMode === 'segments' || effectiveMode === 'variants' || effectiveMode === 'transitions' || effectiveMode === 'dashboard') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('dashboard');
    }

    if (effectiveMode === 'valueSimulation') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('valueSimulation');
    }

    if (effectiveMode === 'workflow') {
      setTopWrapperHeight('0px');
      setVisiblePV(false);
      setVisibleDT(false);
      setVisibleIS(false);
      setVisibleIA(false);
      setComponent('workflow');
    }
  }, [effectiveMode]);

  useApiListener(Event.setActionBar_Visualizer, () => {
    setTopWrapperHeight('75px');
    setComponent('PV');
    setVisiblePV(true);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
  });

  useApiListener(Event.setActionBar_Time, () => {
    setTopWrapperHeight('0px');
    setComponent('time');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
  });

  useApiListener(Event.setActionBar_DecompositionTree, () => {
    setTopWrapperHeight('135px');
    setVisiblePV(false);
    setVisibleDT(true);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('DT');
  });

  useApiListener(Event.setActionBar_Cluster, () => {
    setTopWrapperHeight('175px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(true);
    setVisibleIA(false);
    setComponent('CL');
  });

  useApiListener(Event.setActionBar_InfluenceAnalysis, () => {
    setTopWrapperHeight('175px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(true);
    setComponent('IA');
  });

  useApiListener(Event.setActionBar_DiscoveryRulesSummary, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    // Check mode to determine which discoveryRules type
    if (effectiveMode === 'compliance_discovery_rules') {
      setComponent('compliance_discovery_rules');
    } else {
      setComponent('performance_discovery_rules');
    }
  });

  useApiListener(Event.setActionBar_DiscoveryRulesVisualizer, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('discovery_rules_visualizer');
  });

  useApiListener(Event.setActionBar_Model, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('model');
  });

  useApiListener(Event.setActionBar_Dashboard, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('dashboard');
  });

  useApiListener(Event.setActionBar_Consultant, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('consultant');
  });

  useApiListener(Event.setActionBar_Costs, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('cost_rules');
  });

  useApiListener(Event.setActionBar_Workflow, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('workflow');
  });

  useApiListener(Event.setActionBar_Analytics, () => {
    setTopWrapperHeight('0px');
    setVisiblePV(false);
    setVisibleDT(false);
    setVisibleIS(false);
    setVisibleIA(false);
    setComponent('analytics');
  });

  return (
    <Wrapper>
      <Grid height={topWrapperHeight}>
        <TopWrapper color={visualizerBackground}>
          {component === 'PV' && <ProcessMinerGauges />}
          {component === 'DT' && <DecompositionTree_Options />}
          {component === 'CL' && <Cluster_Options />}
          {component === 'IA' && <InfluenceAnalysis_Options />}
        </TopWrapper>

        <BottomWrapper>
          <Diagram id="gDiagramDivPV" color={visualizerBackground} $visible={visiblePV}></Diagram>
          <Diagram id="gDiagramDivDT" color={visualizerBackground} $visible={visibleDT}></Diagram>
          <Diagram id="gDiagramDivRC" color={visualizerBackground} $visible={visibleIS}></Diagram>
          <Diagram id="gDiagramDivIA" color={visualizerBackground} $visible={visibleIA}></Diagram>

          {component === 'home' && (
            <Home
              appTitle={appTitle}
              modelTitle={getProcessModelTitle()}
              processLabel={processLabel}
              entityLabel={entityLabel}
              appEdition={appEdition}
              editionAccess={editionAccess}
              onNavigate={navigateHomeTarget}
            />
          )}

          {component === 'dashboard' && (
            <ProcessDashboard
              AI={AI}
              aiInsightsState={aiInsightsState}
              setAiInsightsState={setAiInsightsState}
              license={license}
              licenseType={licenseType}
              forgeContext={forgeContext}
              licenseInfo={licenseInfo}
              appEdition={appEdition}
              editionAccess={editionAccess}
              processLabel={processLabel}
              entityLabel={entityLabel}
              customer={customer}
              customerID={customer_id}
              initialTab={effectiveMode === 'activityMetrics' ? 'activityMetrics' : effectiveMode === 'slaMetrics' ? 'slaMetrics' : effectiveMode === 'segments' ? 'segments' : effectiveMode === 'variants' ? 'variants' : effectiveMode === 'transitions' ? 'transitions' : effectiveMode === 'discovery_rules' ? 'performance' : 'metrics'}
              hideNav={true}
              snapshotCount={snapshotCount}
              setSnapshotCount={setSnapshotCount}
              snapshotPanelOpen={snapshotPanelOpen}
              setSnapshotPanelOpen={setSnapshotPanelOpen}
              model={model}
            />
          )}

          {component === 'consultant' && (
            <ConsultantDashboard
              AI={AI}
              aiInsightsState={aiInsightsState}
              setAiInsightsState={setAiInsightsState}
              license={license}
              licenseType={licenseType}
              forgeContext={forgeContext}
              licenseInfo={licenseInfo}
              appEdition={appEdition}
              editionAccess={editionAccess}
              processLabel={processLabel}
              entityLabel={entityLabel}
              customer={customer}
              customerID={customer_id}
              initialTab={
                effectiveMode === 'quickHealthcheck' ||
                effectiveMode === 'processHealth' ||
                effectiveMode === 'trendAnalysis' ||
                effectiveMode === 'aiIntelligence' ||
                effectiveMode === 'aiExecutionStrategy' ||
                effectiveMode === 'automationPacks' ||
                effectiveMode === 'frameworkAssessment' ||
                effectiveMode === 'consultingReport' ||
                effectiveMode === 'consultingWorkspace' ||
                effectiveMode === 'workbenchFindings' ||
                effectiveMode === 'workbenchActions' ||
                effectiveMode === 'processSnapshot'
                  ? mode
                  : 'quickHealthcheck'
              }
              hideNav={true}
              snapshotCount={snapshotCount}
              setSnapshotCount={setSnapshotCount}
              snapshotPanelOpen={snapshotPanelOpen}
              setSnapshotPanelOpen={setSnapshotPanelOpen}
              model={model}
            />
          )}

          {component === 'performance_discovery_rules' && (
            <ProcessDashboard
              AI={AI}
              aiInsightsState={aiInsightsState}
              setAiInsightsState={setAiInsightsState}
              license={license}
              licenseType={licenseType}
              forgeContext={forgeContext}
              licenseInfo={licenseInfo}
              appEdition={appEdition}
              editionAccess={editionAccess}
              processLabel={processLabel}
              entityLabel={entityLabel}
              initialTab='performance'
              hideNav={true}
              reducedPadding={true}
              snapshotCount={snapshotCount}
              setSnapshotCount={setSnapshotCount}
              snapshotPanelOpen={snapshotPanelOpen}
              setSnapshotPanelOpen={setSnapshotPanelOpen}
              model={model}
            />
          )}
          {component === 'compliance_discovery_rules' && (
            <ProcessDashboard
              AI={AI}
              aiInsightsState={aiInsightsState}
              setAiInsightsState={setAiInsightsState}
              license={license}
              licenseType={licenseType}
              forgeContext={forgeContext}
              licenseInfo={licenseInfo}
              appEdition={appEdition}
              editionAccess={editionAccess}
              processLabel={processLabel}
              entityLabel={entityLabel}
              initialTab='compliance'
              hideNav={true}
              reducedPadding={true}
              snapshotCount={snapshotCount}
              setSnapshotCount={setSnapshotCount}
              snapshotPanelOpen={snapshotPanelOpen}
              setSnapshotPanelOpen={setSnapshotPanelOpen}
              model={model}
            />
          )}
          {component === 'discovery_rules_visualizer' && <DiscoveryRules customer_id={customer_id} mode={mode} AppVersion={AppVersion} AI={AI} />}
          {component === 'cost_rules' && <Costs />}
          {component === 'workflow' && <WorkFlow AppVersion={AppVersion} contextPath={contextPath} />}
          {component === 'valueSimulation' && <ValueSimulation processLabel={processLabel} entityLabel={entityLabel} />}
          {component === 'time' && <TimeInActivity />}
          {component === 'analytics' && <Analytics />}
          {component === 'model' && <Model />}
        </BottomWrapper>
      </Grid>
      <canvas hidden id="downloadCanvas" width="1300" height="800" style={{ border: '1px', color: '#000000' }}></canvas>
      <SnapshotListPanel
        isOpen={!!snapshotPanelOpen}
        onClose={() => setSnapshotPanelOpen && setSnapshotPanelOpen(false)}
        modelId={getModelID()}
        AppVersion={AppVersion}
      />
    </Wrapper>
  );
};
